import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';
import { getCanEditCountOfQty } from 'entities/Order/model/selectors/ordersPermissions';
import {
    getSelectedOrderIsLoading,
    getSelectedOrderIsPreSubmission,
} from 'entities/Order/model/selectors/ordersSelectors';
import { UserRole, getUserRole } from 'entities/User';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { OrderInventoryTableRowSkeleton } from 'widgets/InventoryTable/ui';

import { OrderGroupedItemsTableRow } from './OrderGroupedItemsTableRow';

interface OrderGroupedItemsContainerProps {
    items: CatalogItem[];
    orderStatus: OrderStatuses;
}

export const OrderGroupedItemsTable = ({
    items,
    orderStatus,
}: OrderGroupedItemsContainerProps) => {
    const userRole = useSelector(getUserRole);
    const canSeePrices = useSelector(getUserCanSeePrices);
    const isLoading = useSelector(getSelectedOrderIsLoading);
    const isEditCountOfQty = useSelector(getCanEditCountOfQty);
    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);

    const isInReview = orderStatus === OrderStatuses.IN_REVIEW;
    const isHidePrice = userRole === UserRole.MAINTENANCE_TECHNICIAN;

    const showPicker = isPreSubmission && isEditCountOfQty;

    const canSeeInventory =
        userRole !== UserRole.MAINTENANCE_TECHNICIAN && isPreSubmission;

    const { t } = useTranslation();

    return !isLoading ? (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    padding: '12px 21px 10px 24px',
                    height: '38px',
                    boxSizing: 'border-box',
                }}
            >
                <Box sx={{ flex: '52%' }}>
                    <Typography
                        typography="openSans.captionMedium"
                        data-testclass="order-item-title-label"
                    >
                        {t('Item')}
                    </Typography>
                </Box>
                {!isInReview && (
                    <>
                        {canSeeInventory && (
                            <Box
                                sx={{
                                    width: '70px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    textAlign: 'right',
                                    minWidth: '70px',
                                }}
                            >
                                <Typography
                                    typography="openSans.captionMedium"
                                    align="right"
                                    data-testclass="order-items-qty-label"
                                >
                                    {t('Stock')}
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={{
                                width: showPicker ? '120px' : '50px',
                                minWidth: showPicker ? '120px' : '50px',
                                marginLeft: '40px',
                            }}
                        >
                            <Typography
                                typography="openSans.captionMedium"
                                data-testclass="order-items-qty-label"
                                sx={{
                                    textAlign: showPicker ? 'center' : 'right',
                                }}
                            >
                                {isPreSubmission ? t('Qty') : t('AVL')}
                            </Typography>
                        </Box>
                        {canSeePrices && !isHidePrice && (
                            <Box sx={{ width: '100px', minWidth: '100px' }}>
                                <Typography
                                    typography="openSans.captionMedium"
                                    align="right"
                                    data-testclass="order-items-total-label"
                                >
                                    {t('Total')}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <Box>
                {items.map((item) => (
                    <OrderGroupedItemsTableRow key={item.id} item={item} />
                ))}
            </Box>
        </Box>
    ) : (
        <>
            <OrderInventoryTableRowSkeleton />
            <OrderInventoryTableRowSkeleton />
            <OrderInventoryTableRowSkeleton />
        </>
    );
};
